import React from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  QrCodeScanner as QrCodeScannerIcon,
  PersonAddDisabled as PersonAddDisabledIcon,
  QrCode2 as QrCode2Icon,
  Dashboard as DashboardIcon,
  ReceiptLong as ReceiptLongIcon,
  Payments as PaymentsIcon,
  CheckCircle as CheckCircleIcon,
  PhoneIphone as PhoneIphoneIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";

const WhyChooseUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <QrCodeScannerIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: "No Mobile App Required",
      description: "Customers can scan the QR code and place orders instantly. No app download needed.",
      benefit: "Faster adoption",
    },
    {
      icon: <PersonAddDisabledIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: "No Registration Required",
      description: "Customers can order directly without creating an account. Zero friction experience.",
      benefit: "Higher conversion",
    },
    {
      icon: <QrCode2Icon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: "Smart QR Menu",
      description: "Each table has a unique QR code. Customers scan and view the digital menu instantly.",
      benefit: "Contactless dining",
    },
    {
      icon: <DashboardIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: "Live Order Management",
      description: "Real-time order dashboard. Orders go directly to kitchen and staff screens.",
      benefit: "No order misses",
    },
    {
      icon: <ReceiptLongIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: "Easy Billing",
      description: "Fast billing with auto-generated invoices. Track all transactions easily.",
      benefit: "Accurate billing",
    },
    {
      icon: <PaymentsIcon sx={{ fontSize: { xs: 32, md: 40 } }} />,
      title: "Multiple Payment Options",
      description: "Cash, UPI, PhonePe, Counter Payment - flexible payment for every customer.",
      benefit: "Customer convenience",
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 8, md: 12 },
        bgcolor: "#ffffff",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        
        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              color: "var(--primary)",
              fontWeight: 700,
              fontSize: { xs: "13px", sm: "14px", md: "14px" },
              letterSpacing: "2px",
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 2,
                bgcolor: "var(--primary)",
                borderRadius: "2px",
              }}
            />
            Why Choose Us
            <Box
              sx={{
                width: 30,
                height: 2,
                bgcolor: "var(--primary)",
                borderRadius: "2px",
              }}
            />
          </Typography>

          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2rem", sm: "2.8rem", md: "2.8rem" },
              color: "var(--text)",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            Why{" "}
            <Box component="span" sx={{ color: "var(--primary)" }}>
              Restaurant Owners
            </Box>{" "}
            Love Us
          </Typography>

          <Typography
            sx={{
              color: "var(--text-light)",
              fontSize: { xs: "15px", sm: "16px", md: "16px" },
              maxWidth: "600px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            We eliminate friction points and make restaurant management effortless
          </Typography>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {features.map((feature, index) => (
            <Grid item size={{xs:12, sm:6, md:4 }} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 3.5, md: 4 },
                  borderRadius: "24px",
                  height: "100%",
                  bgcolor: "#ffffff",
                  border: "1px solid var(--border)",
                  transition: "all 0.4s ease",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: "var(--shadow-orange)",
                    borderColor: "var(--primary)",
                  },
                }}
              >
                {/* Background decoration */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -60,
                    right: -60,
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(255,107,0,0.04) 0%, transparent 70%)",
                    pointerEvents: "none",
                  }}
                />

                {/* Checkmark badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    width: { xs: 24, md: 28 },
                    height: { xs: 24, md: 28 },
                    borderRadius: "50%",
                    bgcolor: "var(--bg-primary-soft)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--primary)",
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                </Box>

                {/* Icon */}
                <Box
                  sx={{
                    width: { xs: 60, sm: 64, md: 72 },
                    height: { xs: 60, sm: 64, md: 72 },
                    borderRadius: "18px",
                    bgcolor: "var(--bg-primary-soft)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--primary)",
                    mb: { xs: 2, md: 2.5 },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "rgba(255, 107, 0, 0.15)",
                      transform: "scale(1.05) rotate(-5deg)",
                    },
                  }}
                >
                  {feature.icon}
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.3rem" },
                    color: "var(--text)",
                    mb: 1,
                    lineHeight: 1.3,
                  }}
                >
                  {feature.title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    color: "var(--text-light)",
                    fontSize: { xs: "14px", md: "15px" },
                    lineHeight: 1.6,
                    mb: 2,
                  }}
                >
                  {feature.description}
                </Typography>

                {/* Benefit chip */}
                <Chip
                  label={`✓ ${feature.benefit}`}
                  size="small"
                  sx={{
                    bgcolor: "var(--bg-primary-soft)",
                    color: "var(--primary)",
                    fontWeight: 600,
                    fontSize: { xs: "11px", md: "12px" },
                    borderRadius: "8px",
                    "&:hover": {
                      bgcolor: "rgba(255, 107, 0, 0.15)",
                    },
                  }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WhyChooseUs;